import type { KeyboardEvent as ReactKeyboardEvent } from "react";

import { type EmailCardData, type Priority, relativeTime } from "@/lib/emails";
import { cn } from "@/lib/cn";

interface CaseCardProps {
  email: EmailCardData;
  onSelect?: (id: string) => void;
  /** Optional inline action label (Reply / Task / Approve / Route
   *  up). Every card has a verb, per brief §9. */
  verb?: string;
  variant?: "compact" | "expanded";
}

const PRIORITY_CLASSES: Record<Priority, string> = {
  P0: "border-bucket-need-reply-now text-bucket-need-reply-now",
  P1: "border-op-amber text-op-amber",
  P2: "border-op-border-accent text-op-accent",
  P3: "border-op-border text-op-secondary",
};

/** Card representing one merged case (or one ungrouped email) on the
 *  cockpit. Drops the dashboard's stripe-on-left in favor of the
 *  reference design's row layout with entity chips. */
export function CaseCard({
  email,
  onSelect,
  verb,
  variant = "compact",
}: CaseCardProps) {
  const interactive = !!onSelect;
  const chipLabel =
    (email.client_name && email.client_name.trim()) ||
    email.sender_display_name ||
    "Unknown sender";
  const when = relativeTime(email.received_at);
  const handleClick = () => onSelect?.(email.id);
  const handleKey = (e: ReactKeyboardEvent<HTMLElement>) => {
    if (!onSelect) return;
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      onSelect(email.id);
    }
  };

  // The "verb" chip — defaults to a sensible value derived from the
  // triage shape so brief §9 ("every card has a verb") holds even
  // when the caller doesn't override.
  const derivedVerb =
    verb ??
    (email.triage_state === "DRAFTED"
      ? "Approve"
      : email.triage_state === "ESCALATED"
        ? "Route up"
        : email.needs_reply
          ? "Reply"
          : email.routed_to
            ? "Routed"
            : "Open");

  return (
    <article
      className={cn(
        "group relative rounded-lg border border-op-border bg-op-surface px-4 py-3 transition-colors",
        interactive &&
          "cursor-pointer hover:bg-op-elevated focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-op-accent",
        variant === "expanded" && "py-4",
      )}
      data-testid="case-card"
      data-email-id={email.id}
      data-lane={email.lane}
      data-bucket={email.bucket}
      onClick={interactive ? handleClick : undefined}
      onKeyDown={interactive ? handleKey : undefined}
      tabIndex={interactive ? 0 : undefined}
      role={interactive ? "button" : undefined}
      aria-label={interactive ? `Open case ${email.subject || email.id}` : undefined}
    >
      <header className="flex items-start justify-between gap-3">
        <div className="flex min-w-0 flex-1 items-center gap-2">
          <span
            className="truncate rounded-full border border-op-border px-2 py-0.5 text-[10px] uppercase tracking-wider text-op-secondary"
            title={chipLabel}
            data-testid="case-card-chip"
          >
            {chipLabel}
          </span>
          {email.routed_to ? (
            <span
              data-testid="case-card-routed"
              className="rounded-full bg-op-accent-muted px-2 py-0.5 text-[10px] uppercase tracking-wider text-op-accent"
              title={`Routed to ${email.routed_to}`}
            >
              → {email.routed_to}
            </span>
          ) : null}
          {email.escalation_signal ? (
            <span
              data-testid="case-card-escalation"
              className="rounded border border-bucket-need-reply-now px-1.5 py-0.5 font-mono text-[10px] uppercase tracking-wider text-bucket-need-reply-now"
            >
              ESC
            </span>
          ) : null}
        </div>
        <div className="flex shrink-0 items-center gap-2">
          <span
            className={cn(
              "rounded border px-1.5 py-0.5 font-mono text-[10px]",
              PRIORITY_CLASSES[email.priority],
            )}
          >
            {email.priority}
          </span>
          <span className="text-[11px] text-op-muted">{when}</span>
        </div>
      </header>

      <h3
        className="mt-2 truncate text-sm font-medium text-op-primary"
        title={email.subject}
      >
        {email.subject || "(no subject)"}
      </h3>

      {email.summary_line_1 ? (
        <p className="mt-1 line-clamp-2 text-xs leading-snug text-op-secondary">
          {email.summary_line_1}
        </p>
      ) : null}

      <EntityChips entities={email.entities} />

      <footer className="mt-3 flex items-center justify-between gap-2">
        <span className="font-mono text-[10px] uppercase tracking-wider text-op-muted">
          {email.triage_state}
        </span>
        <span className="rounded border border-op-border-accent bg-op-accent-muted px-2 py-0.5 text-[10px] font-medium uppercase tracking-wider text-op-accent">
          {derivedVerb}
        </span>
      </footer>
    </article>
  );
}

function EntityChips({ entities }: { entities: EmailCardData["entities"] }) {
  const tags: Array<{ key: string; value: string }> = [];
  if (entities.lrn) tags.push({ key: "LRN", value: entities.lrn });
  if (entities.po) tags.push({ key: "PO", value: entities.po });
  if (entities.truck) tags.push({ key: "Truck", value: entities.truck });
  if (entities.site) tags.push({ key: "Site", value: entities.site });
  if (entities.amount) tags.push({ key: "Amount", value: entities.amount });
  if (tags.length === 0) return null;
  return (
    <div
      data-testid="case-card-entities"
      className="mt-2 flex flex-wrap items-center gap-1.5"
    >
      {tags.map((t) => (
        <span
          key={`${t.key}-${t.value}`}
          className="rounded border border-op-border bg-op-base px-1.5 py-0.5 font-mono text-[10px] text-op-secondary"
          title={`${t.key} ${t.value}`}
        >
          <span className="text-op-muted">{t.key}</span>
          <span className="ml-1 text-op-primary">{t.value}</span>
        </span>
      ))}
    </div>
  );
}
